/**
 * PrintOps.js — Structured receipt renderer for NATIVE TEXT-MODE printing.
 *
 * WHY: The H10P's Senraise print service exposes a full text API (printText,
 * printTableText, printQRCode, setTextSize/Bold/Alignment, ...). Text mode
 * uses the PRINTER'S BUILT-IN FONT — razor-sharp at any length — and streams
 * line-by-line, so there is no giant-bitmap capture (which also fixes the
 * long-receipt halfway cutoff of the HTML→bitmap path).
 *
 * This module renders a document as a flat list of OPERATIONS. The Java side
 * (H10PPrinterPlugin.printOps) walks the list and calls the SDK 1:1.
 *
 * ── OPS CONTRACT v1 (implemented by H10PPrinterPlugin.java `printOps`) ──────
 *
 * Payload:  { version: 1, ops: [ ...op ] }   (+ feedLinesAfter at top level,
 *                                              same semantics as printHtml)
 * Ops (each maps to ONE PrinterInterface AIDL call):
 *   { op:'align', v:0|1|2 }                  → setAlignment(v)      0=left 1=center 2=right
 *   { op:'size',  v:float }                  → setTextSize(v)       printer px; 24 ≈ normal
 *   { op:'bold',  v:bool }                   → setTextBold(v)
 *   { op:'dw',    v:bool }                   → setTextDoubleWidth(v)
 *   { op:'dh',    v:bool }                   → setTextDoubleHeight(v)
 *   { op:'text',  v:string }                 → printText(v)         v ALWAYS ends with '\n'
 *   { op:'table', cols:[..], weights:[..], aligns:[..] }
 *                                            → printTableText(cols, weights, aligns)
 *   { op:'qr',    data:string, module:int, ecc:int }
 *                                            → printQRCode(data, module, ecc)
 *   { op:'feed',  lines:int }                → nextLine(lines)
 *
 * Java walker rules:
 *   - Wrap the whole list in beginWork()/endWork().
 *   - Unknown `op` values MUST be skipped silently (forward compatibility).
 *   - After the list, apply feedLinesAfter via nextLine() exactly like printHtml.
 *   - Reset state (align 0, size 24, bold false, dw/dh false) before AND after
 *     the walk — printer text state is sticky across jobs.
 *
 * Known v1 limitations (fall back to the HTML→bitmap path for these):
 *   - No image op: signature images and logos are NOT printed in text mode
 *     (signatures are represented by a signed-at/token stamp line instead).
 *   - setDark is NOT emitted (value range undocumented — T2 may experiment).
 *
 * Text conventions: ASCII only — peso is 'P' (formatPHP), multiply is 'x'.
 * The printer charset for non-ASCII glyphs is not guaranteed.
 */
import { formatDate, formatDateTime } from '../lib/dateFormat';
import { lineDescription, docNote } from './docExtras';
import { qtyWithUnit, categorySummaryTextLines } from './printUnits';

export const OPS_SPEC_VERSION = 1;

// Printer text sizes (Senraise setTextSize units — demo app default is 24).
const SIZE_SMALL = 20;
const SIZE_NORMAL = 24;
const SIZE_TITLE = 27;
const SIZE_GRAND = 30;

// ~32 monospace chars per 384-dot line at SIZE_NORMAL.
const DIVIDER = '-'.repeat(32);
// Item detail rows print at SIZE_SMALL (~38 chars) in a 3:2 table, so the
// left column holds ~23 — keep one character of slack.
const QTY_COL_MAX = 22;

const fmtPHP = (v) => {
  const n = parseFloat(v) || 0;
  return `P${n.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
// Per-unit line discount label — matches PrintEngine.perUnitDiscText so the
// text-mode (H10P) receipt shows the discount PER UNIT like every other doc.
const perUnitDiscText = (item) => {
  const type = item.discount_type || 'amount';
  const qty = parseFloat(item.quantity || item.qty) || 0;
  const rate = parseFloat(item.rate || item.unit_price || item.price) || 0;
  let val = parseFloat(item.discount_value) || 0;
  const amt = parseFloat(item.discount_amount) || 0;
  if (type === 'percent') {
    if (val <= 0 && amt > 0 && qty > 0 && rate > 0) val = Math.round((amt / (qty * rate)) * 10000) / 100;
    return val > 0 ? `${val}%` : '';
  }
  if (val <= 0 && amt > 0 && qty > 0) val = Math.round((amt / qty) * 100) / 100;
  return val > 0 ? `${fmtPHP(val)}/unit` : '';
};
const fmtDate = (d) => { if (!d) return ''; try { return formatDate(d); } catch { return String(d); } };
const fmtDateMaybeTime = (d) => {
  if (!d) return '';
  if (typeof d === 'string' && d.includes('T')) { try { return formatDateTime(d); } catch { return String(d); } }
  return fmtDate(d);
};
const stripHtml = (s) => String(s || '').replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').trim();

// ── Op helpers ───────────────────────────────────────────────────────────────
const t = (v) => ({ op: 'text', v: v.endsWith('\n') ? v : v + '\n' });
const row2 = (l, r) => ({ op: 'table', cols: [String(l), String(r)], weights: [3, 2], aligns: [0, 2] });
const align = (v) => ({ op: 'align', v });
const size = (v) => ({ op: 'size', v });
const bold = (v) => ({ op: 'bold', v });
const feed = (lines) => ({ op: 'feed', lines });
const divider = () => t(DIVIDER);

/** Reset sticky printer state — emitted at build start and end. */
const RESET = [align(0), size(SIZE_NORMAL), bold(false), { op: 'dw', v: false }, { op: 'dh', v: false }];

// ── Shared sections ─────────────────────────────────────────────────────────
function headerOps(biz) {
  const ops = [align(1), size(SIZE_TITLE), bold(true), t(biz.business_name || 'AgriBooks'), bold(false), size(SIZE_SMALL)];
  if (biz.address) ops.push(t(biz.address));
  if (biz.phone) ops.push(t(biz.phone));
  if (biz.tin) ops.push(t(`TIN: ${biz.tin}`));
  ops.push(size(SIZE_NORMAL), align(0));
  return ops;
}

function titleOps(label) {
  return [divider(), align(1), bold(true), t(label), bold(false), align(0), divider()];
}

// Mirrors buildItemsThermal 1:1: name line, qty x rate | total, discount
// detail rows — with the default layout's dashed separator between products
// ("this info belongs to this product").
const ITEM_SEP = '- '.repeat(16).trim();
function itemsOps(items) {
  const ops = [];
  let first = true;
  for (const item of items || []) {
    if (!first) ops.push(size(SIZE_SMALL), t(ITEM_SEP), size(SIZE_NORMAL));
    first = false;
    const qty = parseFloat(item.quantity || item.qty) || 0;
    const rate = parseFloat(item.rate || item.unit_price || item.price || item.transfer_capital) || 0;
    const discAmt = parseFloat(item.discount_amount) || 0;
    const grossLine = qty * rate;
    const total = (item.total !== undefined && item.total !== null && item.total !== '')
      ? parseFloat(item.total)
      : (grossLine - discAmt);
    ops.push(t(item.product_name || item.description || ''));
    ops.push(size(SIZE_SMALL));
    // The line's own description. This renderer drives the terminal's BUILT-IN
    // printer font (text mode), so it is a third copy of "how a receipt looks"
    // beside the two in PrintEngine — it had the same blind spot: the
    // description only ever appeared when the product NAME was missing, so a
    // Farm Service line printed as just "Farm Service:". Same rule as the
    // HTML renderers, from lib/docExtras.
    {
      const _desc = item.product_name ? lineDescription(item) : '';
      if (_desc) ops.push(t(`  ${_desc}`));
    }
    // "2 BAGS x P1,890.00" (owner 2026-09-29). A rare long one ("12.5 METERS
    // x P12,345.00") would overflow the table's left column, so the quantity
    // then gets a line of its own and the rate starts the row.
    const qtyText = `  ${qtyWithUnit(qty, item)}`;
    const rateText = `x ${fmtPHP(rate)}`;
    const fits = (qtyText.length + 1 + rateText.length) <= QTY_COL_MAX;
    if (!fits) ops.push(t(qtyText));
    const qtyRate = fits ? `${qtyText} ${rateText}` : `  ${rateText}`;
    if (discAmt > 0) {
      // Per-unit discount (the net Line Total still reflects the whole line).
      const pu = perUnitDiscText(item);
      ops.push(row2(qtyRate, fmtPHP(grossLine)));
      ops.push(row2('  Less Disc.', `-${pu}`));
      ops.push(row2('  Line Total', fmtPHP(total)));
    } else {
      ops.push(row2(qtyRate, fmtPHP(total)));
    }
    ops.push(size(SIZE_NORMAL));
  }
  return ops;
}

// Per-category unit summary (owner 2026-09-29) — same content as the HTML
// block: "PESTICIDE (3)" then "  10 BAGS, 3 L, 5 BOT", wrapped by hand
// (text mode has no word-wrap) on the ", " between units.
function summaryOps(items) {
  const lines = categorySummaryTextLines(items, { width: 30 });
  if (!lines.length) return [];
  const ops = [divider(), size(SIZE_SMALL)];
  for (const l of lines) ops.push(bold(l.bold), t(l.text));
  ops.push(bold(false), size(SIZE_NORMAL));
  return ops;
}

// The document's own note. No format printed a sale's note before 2026-09-23;
// this renderer is the terminal's built-in-font path, so it needs its own
// spelling of it. Wrapped by hand: text mode has no word-wrap of its own and a
// long note would otherwise run off the 32-character roll.
function noteOps(doc, label = 'Note') {
  const note = docNote(doc);
  if (!note) return [];
  const ops = [size(SIZE_SMALL), t(ITEM_SEP), t(`${label}:`)];
  const WIDTH = 30;
  let line = '';
  for (const word of note.split(/\s+/)) {
    if (!line) { line = word; continue; }
    if ((line + ' ' + word).length <= WIDTH) { line += ' ' + word; continue; }
    ops.push(t(line));
    line = word;
  }
  if (line) ops.push(t(line));
  ops.push(size(SIZE_NORMAL));
  return ops;
}

// Senior Citizen / PWD rows + cardholder block (RA 9994 / RA 10754).
function scpwdOps(inv) {
  const sc = inv && inv.scpwd;
  if (!sc || !sc.enabled) return { rows: [], card: [] };
  const pct = sc.discount_percent != null ? sc.discount_percent : 20;
  const rows = [];
  if (parseFloat(sc.vat_amount) > 0) rows.push(row2('Less: VAT', `-${fmtPHP(sc.vat_amount)}`));
  rows.push(row2('VAT-Exempt Sales', fmtPHP(sc.vat_exempt_sales)));
  rows.push(row2(`Less: ${pct}% SC/PWD Disc`, `-${fmtPHP(sc.discount_amount)}`));
  const typeLabel = sc.type === 'pwd' ? 'PWD' : 'Senior Citizen';
  const card = [
    divider(), size(SIZE_SMALL),
    t(`${typeLabel}: ${sc.card_name || ''}`),
    t(`${sc.type === 'pwd' ? 'PWD ID' : 'OSCA/SC ID'}: ${sc.card_id || ''}`),
    t('Signature: ____________'),
    size(SIZE_NORMAL),
  ];
  return { rows, card };
}

function qrOps(docCode, invoiceNumber) {
  const ops = [align(1)];
  if (docCode) {
    const url = `${window.location.origin}/doc/${docCode}`;
    // module 8 (~1mm modules @203dpi). The SDK demo's module 4 printed blurry
    // small squares on the real H10P head (2026-07-13 on-paper test).
    ops.push({ op: 'qr', data: url, module: 8, ecc: 3 });
    ops.push(size(SIZE_SMALL), bold(true), t(docCode), bold(false));
    ops.push(t('Scan to view document'), size(SIZE_NORMAL));
  } else if (invoiceNumber) {
    ops.push({ op: 'qr', data: `Invoice ${invoiceNumber}`, module: 8, ecc: 3 });
    ops.push(size(SIZE_SMALL), t('Scan for reference'), size(SIZE_NORMAL));
  }
  ops.push(align(0));
  return ops;
}

// Internal-document footer (mirrors INTERNAL_FOOTERS in PrintEngine).
const FOOTERS = {
  order_slip: [
    'ORDER SLIP ONLY',
    'For order preparation and stock checking only.',
    'Not an Official Invoice. Not valid for claim of input tax.',
    'Sales Invoice will be issued upon order completion.',
    'For items not available, request a partial refund at the counter.',
  ],
  trust_receipt: [
    'TRUST RECEIPT ONLY',
    'Issued to acknowledge goods received on credit.',
    'Please ask for the corresponding Charge Invoice.',
    'Not an Invoice. Not valid for claim of input tax.',
  ],
};

function footerOps(docKind, { isDraft = false } = {}) {
  const lines = FOOTERS[docKind] || [];
  const ops = [divider(), align(1), size(SIZE_SMALL)];
  if (isDraft) ops.push(bold(true), t('DRAFT - not yet a valid transaction.'), bold(false));
  if (lines.length) {
    ops.push(bold(true), t(lines[0]), bold(false));
    for (const line of lines.slice(1)) ops.push(t(line));
  }
  ops.push(size(SIZE_NORMAL), align(0));
  return ops;
}

function releaseStatusOps(inv) {
  if (inv.release_mode === 'full') return [row2('Status:', 'FULLY RELEASED')];
  if (inv.release_mode === 'partial') {
    return [align(1), bold(true), size(SIZE_SMALL),
      t('* PARTIAL RELEASE *'), t('SCAN QR CODE TO RELEASE ITEMS'),
      size(SIZE_NORMAL), bold(false), align(0)];
  }
  return [];
}

// ── Document builders ────────────────────────────────────────────────────────

function orderSlipOps(inv, biz, docCode, opts = {}) {
  const ops = [...RESET, ...headerOps(biz), ...titleOps('ORDER SLIP')];
  ops.push(row2('No:', inv.invoice_number || ''));
  ops.push(row2('Date:', fmtDateMaybeTime(inv.invoice_date || inv.created_at || inv.order_date)));
  if (inv.customer_name && inv.customer_name !== 'Walk-in') ops.push(row2('Customer:', inv.customer_name));
  if (inv.customer_po) ops.push(row2('Ref PO:', inv.customer_po));
  ops.push(row2('Cashier:', inv.cashier_name || ''));
  ops.push(...releaseStatusOps(inv));
  ops.push(divider());
  ops.push(...itemsOps(inv.items));
  ops.push(divider());
  ops.push(row2('Subtotal', fmtPHP(inv.subtotal)));
  if (inv.overall_discount > 0) ops.push(row2('Discount', `-${fmtPHP(inv.overall_discount)}`));
  const sc = scpwdOps(inv);
  ops.push(...sc.rows);
  ops.push(size(SIZE_GRAND), bold(true), row2('TOTAL', fmtPHP(inv.grand_total)), bold(false), size(SIZE_NORMAL));
  ops.push(...sc.card);
  ops.push(row2('Payment:', inv.payment_method || 'Cash'));
  const tendered = parseFloat(inv.tendered_amount) || 0;
  const change = inv.change_amount != null ? parseFloat(inv.change_amount) || 0
    : Math.max(0, tendered - (parseFloat(inv.grand_total) || 0));
  if (tendered > 0 && inv.payment_type !== 'credit') {
    ops.push(row2('Tendered:', fmtPHP(tendered)));
    if (change > 0) ops.push(row2('Change:', fmtPHP(change)));
  } else if (inv.amount_paid > 0 && inv.payment_type !== 'credit') {
    ops.push(row2('Paid:', fmtPHP(inv.amount_paid)));
  }
  // Add-on payments (amend collect-now) carry their own tendered/change —
  // print each as its own pair; the checkout-time figures above stay frozen.
  for (const p of (inv.payments || [])) {
    if (!p || !(parseFloat(p.tendered) > 0) || !((parseFloat(p.amount) || 0) > 0)) continue;
    ops.push(row2('Add-on Tendered:', fmtPHP(parseFloat(p.tendered) || 0)));
    if (parseFloat(p.change) > 0) ops.push(row2('Add-on Change:', fmtPHP(parseFloat(p.change) || 0)));
  }
  ops.push(...summaryOps(inv.items));
  ops.push(...noteOps(inv));
  if (!opts.omitQr) ops.push(...qrOps(docCode, inv.invoice_number));
  if (!opts.omitFooter) ops.push(...footerOps('order_slip', { isDraft: inv.status === 'for_preparation' }));
  ops.push(...RESET, feed(1));
  return ops;
}

function trustReceiptOps(inv, biz, docCode, opts = {}) {
  const ops = [...RESET, ...headerOps(biz), ...titleOps('CHARGE AGREEMENT')];
  ops.push(row2('No:', inv.invoice_number || ''));
  ops.push(row2('Date:', fmtDateMaybeTime(inv.invoice_date || inv.created_at || inv.order_date)));
  ops.push(row2('Customer:', inv.customer_name || ''));
  if (inv.customer_po) ops.push(row2('Ref PO:', inv.customer_po));
  if (inv.due_date) ops.push(row2('Due:', fmtDate(inv.due_date)));
  ops.push(...releaseStatusOps(inv));
  ops.push(divider());
  ops.push(...itemsOps(inv.items));
  ops.push(divider());
  ops.push(row2('Subtotal', fmtPHP(inv.subtotal)));
  ops.push(size(SIZE_GRAND), bold(true), row2('TOTAL', fmtPHP(inv.grand_total)), bold(false), size(SIZE_NORMAL));
  if (inv.amount_paid > 0) ops.push(row2('Paid', fmtPHP(inv.amount_paid)));
  if (inv.balance > 0) ops.push(bold(true), row2('BALANCE', fmtPHP(inv.balance)), bold(false));
  ops.push(...summaryOps(inv.items));
  const terms = stripHtml((biz.trust_receipt_terms || '').replace('{business_name}', biz.business_name || ''));
  if (terms) {
    ops.push(divider(), align(1), bold(true), size(SIZE_SMALL), t('TERMS'), bold(false), align(0));
    for (const line of terms.split('\n').map(s => s.trim()).filter(Boolean)) ops.push(t(line));
    ops.push(size(SIZE_NORMAL));
  }
  // Signature: text mode has no image op — digital signatures are represented
  // by the tamper-evident stamp; manual signing keeps the blank line.
  ops.push(feed(1), align(1));
  if (inv.signature_url || inv.bypass_method) {
    ops.push(size(SIZE_SMALL));
    if (inv.bypass_method && !inv.signature_url) ops.push(t('[ AUTHORIZED VIA MANAGER PIN ]'));
    else ops.push(t('[ SIGNED DIGITALLY - see doc QR ]'));
    const sa = inv.signature_signed_at || '';
    const tok = inv.signature_verification_token || '';
    if (sa || tok) {
      const dtStr = sa ? new Date(sa).toISOString().slice(0, 16).replace('T', ' ') + 'Z' : '';
      ops.push(t(`Signed ${dtStr}${tok ? ` . v.${tok}` : ''}`));
    }
    ops.push(size(SIZE_NORMAL));
  } else {
    ops.push(t('______________________'));
    ops.push(size(SIZE_SMALL), t('Customer Signature & Printed Name'), size(SIZE_NORMAL));
  }
  ops.push(align(0));
  ops.push(...noteOps(inv));
  if (!opts.omitQr) ops.push(...qrOps(docCode, inv.invoice_number));
  if (!opts.omitFooter) ops.push(...footerOps('trust_receipt'));
  ops.push(...RESET, feed(1));
  return ops;
}

// ── Printer calibration strip ────────────────────────────────────────────────
// One diagnostic print that reveals the SDK's UNDOCUMENTED runtime semantics
// (printTableText weights/aligns, size scale, style flags, QR module sizes).
// The owner reads the paper, reports which lines look right, and the ops
// builders get encoded with MEASURED rules instead of assumptions.
function calibrationOps() {
  // PLAIN-WORDS edition (owner request): every test says in real words what
  // it is trying, using realistic receipt content. The owner reads the paper
  // and reports e.g. "Column test 2 puts the price at the right edge" or
  // "Size 24 is even, size 20 is patchy".
  const ops = [...RESET];
  const say = (s) => { ops.push(size(SIZE_SMALL), t(s), size(SIZE_NORMAL)); };
  ops.push(align(1), bold(true), t('PRINTER TEST PAGE'), bold(false), align(0));
  say('Read each test and tell me');
  say('which ones look correct.');
  ops.push(t(DIVIDER));

  ops.push(bold(true), t('COLUMN WIDTH TESTS'), bold(false));
  say('Goal: product left, price at the');
  say('RIGHT PAPER EDGE, one line.');
  say('');
  say('Column test 1 (half / half):');
  ops.push({ op: 'table', cols: ['10 x P1,150.00', 'P11,500.00'], weights: [1, 1], aligns: [0, 2] });
  say('Column test 2 (wide left 60/40):');
  ops.push({ op: 'table', cols: ['10 x P1,150.00', 'P11,500.00'], weights: [3, 2], aligns: [0, 2] });
  say('Column test 3 (wider left 2/3):');
  ops.push({ op: 'table', cols: ['10 x P1,150.00', 'P11,500.00'], weights: [2, 1], aligns: [0, 2] });
  say('Column test 4 (long name wraps?):');
  ops.push({ op: 'table', cols: ['Ammonium Sulfate 21-0-0 50kg', 'P1,150.00'], weights: [3, 2], aligns: [0, 2] });
  ops.push(t(DIVIDER));

  ops.push(bold(true), t('POSITION TESTS'), bold(false));
  say('Position test A - both words LEFT:');
  ops.push({ op: 'table', cols: ['left-word', 'also-left?'], weights: [1, 1], aligns: [0, 0] });
  say('Position test B - price to RIGHT:');
  ops.push({ op: 'table', cols: ['left-word', 'right-edge?'], weights: [1, 1], aligns: [0, 2] });
  say('Position test C - middle CENTERED?:');
  ops.push({ op: 'table', cols: ['left', 'centered?', 'right'], weights: [1, 1, 1], aligns: [0, 1, 2] });
  ops.push(t(DIVIDER));

  ops.push(bold(true), t('TEXT SIZE TESTS'), bold(false));
  say('Which sizes print EVEN thickness?');
  say('Which look patchy / broken?');
  for (const s of [18, 20, 24, 28, 32]) {
    ops.push(size(s), t(`Size ${s}: Hog Grower P1,890.00`));
  }
  ops.push(size(SIZE_NORMAL));
  ops.push(t(DIVIDER));

  ops.push(bold(true), t('THICKNESS TESTS'), bold(false));
  ops.push(t('Normal: Corn Grits 50kg'));
  ops.push(bold(true), t('Bold: Corn Grits 50kg'), bold(false));
  ops.push({ op: 'dw', v: true }, t('Double wide'), { op: 'dw', v: false });
  ops.push({ op: 'dh', v: true }, t('Double tall'), { op: 'dh', v: false });
  ops.push({ op: 'dw', v: true }, { op: 'dh', v: true }, t('BIG (TOTAL row)'), { op: 'dw', v: false }, { op: 'dh', v: false });

  ops.push(t(DIVIDER));
  ops.push(align(1), bold(true), t('END OF TEST PAGE'), bold(false), align(0));
  ops.push(...RESET, feed(2));
  return ops;
}

const BUILDERS = {
  order_slip: orderSlipOps,
  trust_receipt: trustReceiptOps,
};

const PrintOps = {
  /** Doc types renderable in text mode. Others fall back to HTML→bitmap. */
  canRenderOps(type) {
    return Object.prototype.hasOwnProperty.call(BUILDERS, type);
  },

  /**
   * Render a document as a v1 ops payload for H10PPrinter.printOps.
   * Throws if the type has no ops builder — callers gate on canRenderOps().
   */
  generateOps({ type, data, businessInfo = {}, docCode = '', omitQr = false, omitFooter = false }) {
    const builder = BUILDERS[type];
    if (!builder) throw new Error(`PrintOps: no ops builder for type "${type}"`);
    // omitQr/omitFooter: the H10P's native printQRCode renders BLURRY and small
    // text sizes print with inconsistent thickness (2026-07-14 tests) — the
    // bridge prints QR + disclaimer as a crisp bitmap TAIL job instead, in the
    // correct order: body (text ops) → QR → disclaimer.
    return { version: OPS_SPEC_VERSION, ops: builder(data, businessInfo, docCode, { omitQr, omitFooter }) };
  },

  /** Footer/disclaimer lines for the bitmap tail job (hybrid text mode). */
  getFooterLines(type, { isDraft = false } = {}) {
    const lines = FOOTERS[type] || [];
    return { title: lines[0] || '', body: lines.slice(1), isDraft };
  },

  /** Diagnostic strip revealing the SDK's real table/size/QR semantics. */
  generateCalibrationOps() {
    return { version: OPS_SPEC_VERSION, ops: calibrationOps() };
  },
};

export default PrintOps;
